/**
 * Course configuration: 12 holes + pit stops
 * Shared across Dashboard, Play, Admin
 *
 * Optional per hole: logoUrl — public path (e.g. '/sponsors/umb.webp') or absolute https URL.
 *
 * Collaborative holes: set `sponsors: [{ venue, logoUrl? }, ...]` (2+). Omitted logoUrl uses initials.
 * `holeVenueTitleLine` — one display string (sponsors joined with ·). `holeVenueLabel` — comma-joined for aria.
 */

/** @param {{ venue?: string, sponsors?: Array<{ venue: string, logoUrl?: string | null }>, logoUrl?: string }} hole */
export function holeVenueLabel(hole) {
  if (Array.isArray(hole.sponsors) && hole.sponsors.length > 0) {
    return hole.sponsors.map((s) => s.venue).join(', ')
  }
  return hole.venue ?? ''
}

/**
 * Primary venue line for cards/headers: sponsors joined with middle dot.
 * Prefers `shortVenue` (card title) over full legal `venue` when provided —
 * the long form stays available for a11y/alt via `holeVenueLabel`.
 * May contain `\n` for mobile-only hard breaks (`whitespace-pre-line sm:whitespace-normal`).
 */
export function holeVenueTitleLine(hole) {
  if (Array.isArray(hole.sponsors) && hole.sponsors.length > 0) {
    return hole.sponsors.map((s) => s.shortVenue || s.venue).join(' · ')
  }
  return hole.shortVenue || hole.venue || ''
}

/**
 * Inline variant of the title line with `\n` collapsed to spaces —
 * for contexts where a hard break would look off (e.g. "scored X on Y").
 */
export function holeVenueTitleInline(hole) {
  return holeVenueTitleLine(hole).replace(/\s*\n\s*/g, ' ')
}

/** Each business name as separate string (e.g. structured data). */
export function holeVenueLines(hole) {
  if (Array.isArray(hole.sponsors) && hole.sponsors.length > 0) {
    return hole.sponsors.map((s) => s.venue)
  }
  return [hole.venue ?? '']
}

/** Normalized list for SponsorLogo row(s). */
export function holeSponsorEntries(hole) {
  if (Array.isArray(hole.sponsors) && hole.sponsors.length > 0) {
    return hole.sponsors
  }
  return [{ venue: hole.venue ?? '', logoUrl: hole.logoUrl ?? null }]
}

/**
 * 2026 course — 12 scoring holes, interleaved with 3 pit stops (DB `pit_stops`)
 * and closed out at the Final Party (Hotel Julien). Map route positions 1-15
 * on the printed poster correspond to these hole ids plus pit stops at route
 * positions 6, 9, 14.
 */
export const HOLES = [
  {
    id: 1,
    name: 'Hole 1',
    venue: 'UMB Bank',
    difficulty: 'Easy',
    par: 3,
    address: '700 Locust St, Dubuque, IA 52001',
    logoUrl: '/sponsors/umb-logo.svg',
  },
  {
    id: 2,
    name: 'Hole 2',
    venue: 'Oddfellows Bar Tavern Lounge',
    difficulty: 'Easy',
    par: 3,
    address: '481 Locust St, Dubuque, IA 52001',
    logoUrl: '/sponsors/oddfellows-logo.svg',
  },
  {
    id: 3,
    name: 'Hole 3',
    difficulty: 'Medium',
    par: 4,
    address: '467 Bluff St, Dubuque, IA 52001',
    sponsors: [
      { venue: 'Lovell & Co', logoUrl: '/sponsors/lovell-logo.svg' },
      { venue: 'Capra Bank', logoUrl: '/sponsors/capra-logo.svg' },
    ],
  },
  {
    id: 4,
    name: 'Hole 4',
    venue: 'Elevated Images',
    difficulty: 'Easy',
    par: 3,
    address: '471 W 4th St, Dubuque, IA 52001',
    logoUrl: '/sponsors/elevated-images-logo.svg',
  },
  {
    id: 5,
    name: 'Hole 5',
    venue: 'Goldfinch Wealth Management',
    difficulty: 'Medium',
    par: 4,
    address: '470 W 4th St, Dubuque, IA 52001',
    logoUrl: '/sponsors/goldfinch-logo.svg',
  },
  {
    id: 6,
    name: 'Hole 6',
    difficulty: 'Medium',
    par: 4,
    address: '75 Locust St, Dubuque, IA 52001',
    sponsors: [
      { venue: 'Lane Madsen State Farm', logoUrl: '/sponsors/madsen-logo.svg' },
      { venue: 'Nova Group Real Estate', logoUrl: '/sponsors/nova-group-logo.svg' },
    ],
  },
  {
    id: 7,
    name: 'Hole 7',
    venue: 'Dimensional Brewing Company',
    difficulty: 'Hard',
    par: 5,
    address: '67 Main St, Dubuque, IA 52001',
    logoUrl: '/sponsors/dimensional-logo.svg',
  },
  {
    id: 8,
    name: 'Hole 8',
    venue: 'The Dungeon',
    difficulty: 'Hard',
    par: 5,
    address: '302 Locust St, Dubuque, IA 52001',
    logoUrl: '/sponsors/dungeon-logo.svg',
  },
  {
    id: 9,
    name: 'Hole 9',
    venue: 'The Dungeon 2nd Hole',
    shortVenue: 'The Dungeon\n2nd Hole',
    difficulty: 'Easy',
    par: 3,
    address: '302 Locust St, Dubuque, IA 52001',
    logoUrl: '/sponsors/dungeon-logo.svg',
  },
  {
    id: 10,
    name: 'Hole 10',
    venue: 'Ludovissy & Associates Insurance',
    shortVenue: 'Ludovissy & Associates',
    difficulty: 'Medium',
    par: 4,
    address: '302 Locust St, Dubuque, IA 52001',
    logoUrl: '/sponsors/ludovissy-logo.svg',
    orientation: 'wide',
  },
  {
    id: 11,
    name: 'Hole 11',
    venue: 'River Rock Kitchen & Tap',
    shortVenue: 'River Rock\nKitchen & Tap',
    difficulty: 'Medium',
    par: 4,
    address: '450 Main St, Dubuque, IA 52001',
    logoUrl: '/sponsors/riverrock-logo.svg',
  },
  {
    id: 12,
    name: 'Hole 12',
    venue: 'Hotel Julien Dubuque',
    difficulty: 'Easy',
    par: 3,
    address: '200 Main St, Dubuque, IA 52001',
    logoUrl: '/sponsors/julien-logo.svg',
  },
]

/** Final party venue (raffle, announcements). Shares the Hotel Julien address. */
export const FINAL_ENCORE_PARTY = {
  name: 'Hotel Julien Dubuque',
  address: '200 Main St, Dubuque, IA 52001',
}

export const TOTAL_PAR = HOLES.reduce((sum, hole) => sum + hole.par, 0)

export const VALID_HOLE_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
